/**
 * Shared notification display maps (icon + accent color) keyed by notification
 * type. Used by both {@link NotificationBell} and {@link NotificationCard} so the
 * two presentations stay in sync — the canonical list of types lives in the API's
 * `NotificationService.NotificationType`.
 */
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Car,
  CheckCircle,
  Clock,
  CreditCard,
  AlertTriangle,
  FileText,
  MessageSquare,
  Plane,
  UserPlus,
  XCircle,
} from "lucide-react";

/** Icon to render for each notification type (callers fall back to `Bell`). */
export const typeIcons: Record<string, LucideIcon> = {
  // Invitations & team
  invitation_received: Building2,
  invitation_accepted: UserPlus,
  // Onboarding / approvals
  driver_approved: CheckCircle,
  driver_rejected: XCircle,
  business_approved: CheckCircle,
  business_rejected: XCircle,
  // Document portability
  document_requested: FileText,
  document_access_granted: FileText,
  document_access_revoked: FileText,
  document_expiring: AlertTriangle,
  // Reservation lifecycle
  reservation_offered: Car,
  reservation_driver_assigned: Car,
  reservation_confirmed_by_driver: CheckCircle,
  reservation_declined_by_driver: XCircle,
  reservation_vehicle_changed: Car,
  reservation_cancelled: XCircle,
  reservation_reminder: Clock,
  // Flight tracking
  flight_delayed: Plane,
  flight_cancelled: Plane,
  // Billing & subscription
  payment_failed: CreditCard,
  subscription_upgraded: CheckCircle,
  subscription_cancelled: AlertTriangle,
  // Company → driver broadcast
  dispatch_message: MessageSquare,
  // Two-way messaging
  new_message: MessageSquare,
  // Admin / generic
  admin_message: MessageSquare,
};

/** Accent color classes for each notification type (falls back to primary). */
export const typeColors: Record<string, string> = {
  driver_approved: "text-success-600",
  business_approved: "text-success-600",
  subscription_upgraded: "text-success-600",
  reservation_confirmed_by_driver: "text-success-600",
  document_access_granted: "text-success-600",
  driver_rejected: "text-destructive-600",
  business_rejected: "text-destructive-600",
  reservation_declined_by_driver: "text-destructive-600",
  reservation_cancelled: "text-destructive-600",
  flight_cancelled: "text-destructive-600",
  flight_delayed: "text-warning-600",
  payment_failed: "text-destructive-600",
  subscription_cancelled: "text-destructive-600",
  document_access_revoked: "text-destructive-600",
  document_expiring: "text-warning-600",
  admin_message: "text-primary-600",
  dispatch_message: "text-primary-600",
};

/** Default accent color when a type has no explicit mapping. */
export const DEFAULT_TYPE_COLOR = "text-primary-600";

/**
 * In-app deep link for notifications that point at a specific resource the
 * recipient can act on. Currently: ride offers land directly on that ride
 * request in My Rides (`?tab=requests&ride=<id>` — the page ignores unknown
 * params and only matches `ride` against the driver's already-authorized
 * rides, so the id is treated as an opaque string).
 * @param notification - The notification (type + resource reference).
 * @param notification.type - The notification type.
 * @param notification.resourceType - The referenced resource's type, if any.
 * @param notification.resourceId - The referenced resource's id, if any.
 * @returns A client-side route to navigate to, or null when the notification
 * has no deep link (callers keep their existing behavior).
 */
export function getNotificationDeepLink(notification: {
  type: string;
  resourceType?: string | null;
  resourceId?: string | null;
}): string | null {
  if (
    notification.type === "reservation_offered" &&
    notification.resourceType === "reservation" &&
    notification.resourceId
  ) {
    return `/account/rides?tab=requests&ride=${encodeURIComponent(notification.resourceId)}`;
  }
  return null;
}
